import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, Smartphone } from "lucide-react";
import Button from "../../../components/common/Button/Button";
import StickyActionBar from "../../../components/common/stickyActionBar/StickyActionBar";
import { Card } from "../../../components/ui/card";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import useScreenResolution from "../../../hooks/useScreenResolution";
import { selectNewMotorForm } from "../../../redux/features/newMotorFormSlice";
import { CHANGE_MOBILE_FLOW_UPDATED_MOBILE_NUMBER } from "./changeMobileNumberPage";
// The success mark's pop and check draw, shared with the sign-up success page.
import "../RegisterSuccessPage/RegisterSuccessPage.css";

// Bahraini mobiles are 8 digits: show them as +973 XXXX XXXX.
const formatBahrainMobile = (value = "") => {
  const digits = `${value}`.replace(/\D/g, "");
  const local = digits.length === 11 && digits.startsWith("973") ? digits.slice(3) : digits;
  return local.length === 8 ? `+973 ${local.slice(0, 4)} ${local.slice(4)}` : value;
};

/**
 * Mobile number changed.
 *
 * The number itself is the point of the page, so it is shown, not just
 * announced: a confirmation card with the new number and a Verified chip (the
 * OTP just proved it). Same visual language as the sign-up success page but
 * quieter (no burst or ripple): a routine change, not a milestone. The mark
 * pops, then the message and the card rise in, in that order.
 */
const UpdateSucccessPage = () => {
  const translate = useLanguageTranslate();
  const navigate = useNavigate();
  const { mobile } = useScreenResolution();
  const form = useSelector(selectNewMotorForm);
  const newNumber = form?.[CHANGE_MOBILE_FLOW_UPDATED_MOBILE_NUMBER]?.phone;

  const backToProfile = () => {
    navigate(mobile ? "/profile-details" : "/profile-settings");
  };

  return (
    <div className="mx-auto w-[500px] max-w-full px-5 pt-24 pb-10 max767:pt-[104px]">
      <div className="rs-pop mx-auto mb-6 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[#E8F7EF]">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1E8E5A] shadow-[0_6px_16px_rgba(30,142,90,0.28)]">
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-6 w-6"
            fill="none"
            stroke="#fff"
            strokeWidth="2.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path className="rs-check" d="M5 12.5l4.5 4.5L19 7.5" />
          </svg>
        </span>
      </div>

      <div className="rs-rise text-center" style={{ animationDelay: "140ms" }}>
        <h1 className="text-[#1A2024] text-2xl font-semibold leading-8 tracking-[-0.24px] [text-wrap:balance]">
          {translate("Mobile number updated", "تم تحديث رقم الهاتف")}
        </h1>
        <p className="mx-auto mt-2 max-w-[38ch] text-[#5B6871] text-base leading-6 [text-wrap:balance]">
          {translate(
            "Codes and updates will now go to this number.",
            "ستصلك رموز التحقق والتحديثات على هذا الرقم من الآن."
          )}
        </p>
      </div>

      {newNumber && (
        <Card
          className="rs-rise mt-8 flex items-center gap-3 rounded-[14px] border-[#E5E9EB] p-4 shadow-none"
          style={{ animationDelay: "220ms" }}
        >
          <span
            aria-hidden="true"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[#E6F6FD]"
          >
            <Smartphone className="h-5 w-5 text-[#0A7CAE]" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[#6C7A83] text-[13px] leading-[18px]">
              {translate("New mobile number", "رقم الهاتف الجديد")}
            </p>
            <p
              dir="ltr"
              className="text-[#1A2024] text-lg font-semibold leading-7 tabular-nums rtl:text-right"
            >
              {formatBahrainMobile(newNumber)}
            </p>
          </div>
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-[#E8F7EF] px-2.5 py-1 text-[#1E8E5A] text-xs font-semibold leading-4">
            <BadgeCheck aria-hidden="true" className="h-3.5 w-3.5" />
            {translate("Verified", "تم التحقق")}
          </span>
        </Card>
      )}

      <div className="mt-8">
        <StickyActionBar>
          <Button
            type="submit"
            className="primary-button !mb-0 transition-[background-color,transform] duration-150 ease-out active:scale-[0.98] disabled:active:scale-100"
            onSubmit={backToProfile}
            label={translate("Back to profile", "العودة إلى الملف الشخصي")}
          />
        </StickyActionBar>
      </div>
    </div>
  );
};

export default UpdateSucccessPage;
